import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { ArrowLeft, Pencil, ChevronDown, ChevronUp, Plus, X } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import {
  TEMPLATE_SECTIONS, AFWERKING_PROFILES, FILL_MATERIALS, fillColor, fieldValue, isFieldSet,
  sectionCounts, profileSegments, validateProfile,
} from '../utils/template-sections.js';
import './searchable-select.js';

/** Venster-event met de naam van het sjabloon, voor het kruimelpad. detail: { name } */
export const TEMPLATE_NAME_EVENT = 'mv-template-name';

/**
 * Sjabloon-editor als eigen pagina (routes /templates/new en /templates/:id).
 *
 * Alle onderdelen staan onder elkaar op één pagina, met rechts een inhoudsopgave die laat zien
 * hoeveel er per onderdeel is ingesteld en waar je bent. De velden en hun keuzes komen uit
 * utils/template-sections.js, net als de samenvattingen in het overzicht.
 */
export class TemplateEditor extends LitElement {
  static styles = css`
    :host { display:block; max-width:1120px; }
    .layout { display:flex; flex-wrap:wrap; gap:24px 32px; align-items:flex-start; }
    .main { flex:999 1 520px; min-width:0; display:flex; flex-direction:column; gap:16px; }
    .back { display:inline-flex; align-items:center; gap:6px; border:none; background:none; padding:0; color:var(--mv-text-sub); font:inherit; font-size:0.9rem; cursor:pointer; width:fit-content; }
    .back:hover { color:var(--mv-text); }
    .eyebrow { font-size:0.7rem; font-weight:700; letter-spacing:0.07em; color:var(--mv-text-muted); text-transform:uppercase; }
    .head { display:flex; flex-direction:column; gap:10px; }
    .name-btn { display:flex; align-items:center; gap:12px; max-width:100%; border:1px solid transparent; border-radius:9px; padding:4px 10px; margin-left:-11px; background:transparent; cursor:text; text-align:left; color:var(--mv-text-muted); font:inherit; }
    .name-btn:hover { border-color:var(--mv-border); background:var(--mv-surface); }
    .name-text { font-size:clamp(22px,2.4vw,30px); font-weight:700; letter-spacing:-0.03em; color:var(--mv-text); min-width:0; overflow-wrap:anywhere; }
    .name-text.empty { color:var(--mv-text-muted); }
    .name-input { width:100%; box-sizing:border-box; font:inherit; font-size:clamp(22px,2.4vw,30px); font-weight:700; letter-spacing:-0.03em; color:var(--mv-text);
      border:1px solid var(--mv-primary); box-shadow:0 0 0 3px var(--mv-primary-tint); border-radius:9px; padding:4px 10px; margin-left:-11px; background:var(--mv-surface); outline:none; }
    .lead { margin:0; max-width:72ch; font-size:0.875rem; line-height:1.6; color:var(--mv-text-sub); }

    section { background:var(--mv-surface); border:1px solid var(--mv-border-soft, var(--mv-border)); border-radius:14px; box-shadow:0 5px 15px rgba(0,0,0,.02); padding:22px 26px 26px; scroll-margin-top:16px; }
    .sec-head { display:flex; align-items:center; gap:12px; }
    .num { width:26px; height:26px; border-radius:7px; background:var(--mv-primary-tint); color:var(--mv-primary); font-size:0.75rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex:none; }
    h2 { margin:0; font-size:1.06rem; font-weight:700; letter-spacing:-0.02em; flex:1; min-width:0; color:var(--mv-text); }
    .count { font-size:0.78rem; font-weight:600; padding:4px 9px; border-radius:999px; white-space:nowrap; color:var(--mv-text-sub); background:var(--mv-surface-alt); }
    .count.on { color:var(--mv-primary); background:var(--mv-primary-tint); }
    .uitleg { border:1px solid var(--mv-border-soft, var(--mv-border)); background:var(--mv-surface); border-radius:8px; height:30px; padding:0 10px; display:flex; align-items:center; gap:5px; font:inherit; font-size:0.78rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer; }
    .uitleg:hover { color:var(--mv-text); }
    .desc { margin:14px 0 0; max-width:75ch; font-size:0.845rem; line-height:1.6; color:var(--mv-text-sub); }
    .desc b { color:var(--mv-text); }

    .group { margin-top:20px; }
    .group-title { font-size:0.7rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--mv-text-muted); padding-bottom:10px; margin-bottom:14px; border-bottom:1px solid var(--mv-border-soft, var(--mv-border)); }
    .grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 240px), 1fr)); gap:18px 20px; }
    .field { display:flex; flex-direction:column; gap:7px; min-width:0; }
    .field.wide { grid-column:1 / -1; }
    .f-head { display:flex; align-items:center; gap:8px; min-height:18px; }
    .dot { width:6px; height:6px; border-radius:50%; background:var(--mv-border); flex:none; }
    .dot.on { background:var(--mv-primary); }
    .f-head label { font-size:0.8125rem; font-weight:600; color:var(--mv-text); }
    .f-head .grow { flex:1; }
    .clear { border:none; background:none; padding:0; font:inherit; font-size:0.75rem; font-weight:500; color:var(--mv-text-muted); cursor:pointer; }
    .clear:hover { color:var(--mv-text-sub); }
    .hint { font-size:0.78rem; line-height:1.5; color:var(--mv-text-sub); }

    .ctl { width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:9px; padding:10px 12px; font:inherit; font-size:0.875rem; color:var(--mv-text); background:var(--mv-surface); }
    .ctl.unset { color:var(--mv-text-muted); background:var(--mv-surface-alt); }
    .ctl:focus { outline:none; border-color:var(--mv-primary); box-shadow:0 0 0 3px var(--mv-primary-tint); }
    .sel { position:relative; }
    .sel select { appearance:none; -webkit-appearance:none; padding-right:36px; cursor:pointer; }
    .sel .chev { position:absolute; right:12px; top:50%; transform:translateY(-50%); pointer-events:none; color:var(--mv-text-muted); display:flex; }
    .unit-wrap { position:relative; }
    .unit-wrap .unit { position:absolute; right:12px; top:50%; transform:translateY(-50%); font-size:0.8rem; color:var(--mv-text-muted); pointer-events:none; }
    .unit-wrap input { padding-right:44px; }

    .seg { display:flex; gap:2px; padding:3px; background:var(--mv-surface-alt); border-radius:9px; width:fit-content; max-width:100%; }
    .seg button { border:none; border-radius:7px; padding:7px 14px; font:inherit; font-size:0.8125rem; font-weight:500; color:var(--mv-text-sub); background:transparent; cursor:pointer; white-space:nowrap; }
    .seg button.on { background:var(--mv-surface); color:var(--mv-text); font-weight:600; box-shadow:0 1px 2px rgba(24,28,50,.08), 0 0 0 1px var(--mv-border); }
    .seg button.on.none { color:var(--mv-text-sub); }

    .toggle { display:flex; align-items:center; gap:12px; border:1px solid var(--mv-border); border-radius:9px; padding:11px 14px; background:var(--mv-surface-alt); cursor:pointer; text-align:left; width:100%; font:inherit; }
    .toggle.on { background:var(--mv-surface); }
    .track { width:34px; height:18px; border-radius:999px; background:color-mix(in srgb, var(--mv-text-muted) 45%, transparent); position:relative; flex:none; transition:background .15s; }
    .toggle.on .track { background:var(--mv-primary); }
    .knob { position:absolute; top:2px; left:2px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 2px rgba(0,0,0,.2); transition:transform .15s; }
    .toggle.on .knob { transform:translateX(16px); }
    .toggle .t-label { font-size:0.875rem; font-weight:500; color:var(--mv-text); }

    .profiles { display:grid; grid-template-columns:repeat(auto-fit, minmax(min(100%, 420px), 1fr)); gap:20px; margin-top:20px; }
    .profile { border:1px solid var(--mv-border-soft, var(--mv-border)); border-radius:12px; padding:18px; display:flex; gap:18px; min-width:0; }
    .bar-col { display:flex; flex-direction:column; align-items:center; gap:6px; flex:none; }
    .bar-col .pct { font-size:0.7rem; color:var(--mv-text-muted); }
    .bar { width:34px; height:200px; border-radius:6px; background:var(--mv-surface-alt); border:1px solid var(--mv-border-soft, var(--mv-border)); position:relative; overflow:hidden; }
    .bar.empty { border-style:dashed; border-color:var(--mv-border); }
    .bar .seg-fill { position:absolute; left:0; right:0; border-bottom:1px solid rgba(255,255,255,.7); }
    .p-body { flex:1; min-width:0; display:flex; flex-direction:column; gap:10px; }
    .p-title { font-size:0.875rem; font-weight:700; color:var(--mv-text); }
    .rows { display:grid; grid-template-columns:64px 64px minmax(0,1fr) 28px; gap:6px 8px; align-items:center; }
    .rows .th { font-size:0.7rem; font-weight:700; letter-spacing:0.07em; color:var(--mv-text-muted); }
    .rows input, .rows select { width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:8px; padding:8px; font:inherit; font-size:0.845rem; background:var(--mv-surface); color:var(--mv-text); }
    .mat { position:relative; min-width:0; }
    .mat .swatch { position:absolute; left:10px; top:50%; margin-top:-5px; width:10px; height:10px; border-radius:3px; pointer-events:none; box-shadow:inset 0 0 0 1px rgba(0,0,0,.12); }
    .mat select { appearance:none; -webkit-appearance:none; padding:8px 10px 8px 28px; cursor:pointer; text-overflow:ellipsis; }
    .del { border:none; background:none; width:28px; height:28px; border-radius:7px; display:flex; align-items:center; justify-content:center; color:var(--mv-text-muted); cursor:pointer; }
    .del:hover { background:var(--mv-danger-bg, #fff5f8); color:var(--mv-danger, #f1416c); }
    .msg { font-size:0.78rem; font-weight:500; }
    .msg.ok { color:var(--mv-primary); }
    .msg.bad { color:var(--mv-danger, #d9214e); }
    .none { font-size:0.8125rem; color:var(--mv-text-sub); }
    .add { display:flex; align-items:center; gap:6px; border:1px dashed var(--mv-border-dashed, var(--mv-border)); background:none; border-radius:8px; padding:7px 12px; font:inherit; font-size:0.8125rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer; width:fit-content; }
    .add:hover { border-color:var(--mv-primary); color:var(--mv-primary); }

    aside { flex:1 1 240px; max-width:100%; position:sticky; top:16px; display:flex; flex-direction:column; gap:14px; padding-top:38px; }
    .index { background:var(--mv-surface); border:1px solid var(--mv-border-soft, var(--mv-border)); border-radius:14px; padding:14px; display:flex; flex-direction:column; gap:2px; }
    .index-head { display:flex; justify-content:space-between; align-items:baseline; gap:8px; padding:2px 8px 10px; }
    .index-head .eyebrow { font-size:0.7rem; }
    .index-head .total { font-size:0.75rem; color:var(--mv-text-sub); white-space:nowrap; }
    .idx { display:flex; align-items:center; gap:10px; border:none; border-radius:8px; padding:8px; background:transparent; cursor:pointer; text-align:left; width:100%; font:inherit; }
    .idx:hover { background:var(--mv-surface-alt); }
    .idx.on { background:var(--mv-primary-tint); }
    .idx .n { width:20px; height:20px; border-radius:6px; font-size:0.7rem; font-weight:700; display:flex; align-items:center; justify-content:center; background:var(--mv-surface-alt); color:var(--mv-text-muted); flex:none; }
    .idx.set .n { background:var(--mv-primary-tint); color:var(--mv-primary); }
    .idx.on .n { background:var(--mv-primary); color:#fff; }
    .idx .t { flex:1; font-size:0.845rem; font-weight:500; color:var(--mv-text-sub); }
    .idx.on .t { font-weight:600; color:var(--mv-text); }
    .idx .c { font-size:0.75rem; color:var(--mv-text-muted); }
    .idx.set .c { color:var(--mv-primary); }
    .buttons { display:flex; flex-direction:column; gap:8px; }
    .save { background:var(--mv-primary); color:#fff; border:none; border-radius:9px; padding:11px 16px; font:inherit; font-size:0.875rem; font-weight:600; cursor:pointer; transition:background .15s, color .15s; }
    .save:hover:not(:disabled) { background:var(--mv-primary-hover, var(--mv-primary)); }
    .save:disabled { background:var(--mv-surface-alt); color:var(--mv-text-muted); cursor:default; }
    .cancel { background:var(--mv-surface); color:var(--mv-text); border:1px solid var(--mv-border); border-radius:9px; padding:10px 16px; font:inherit; font-size:0.875rem; font-weight:500; cursor:pointer; }
    .unsaved { font-size:0.78rem; color:var(--mv-warning-text, #8a5b10); text-align:center; padding-top:2px; }
    .error { color:var(--mv-danger, #d33); font-size:0.85rem; }
    .loading { padding:40px; text-align:center; color:var(--mv-text-sub); }
  `;

  static properties = {
    // 'new' of een template-id.
    templateId: { type: String },
    editing: { state: true },
    loading: { state: true },
    saving: { state: true },
    error: { state: true },
    _active: { state: true },        // onderdeel dat nu in beeld is (inhoudsopgave)
    _closedDesc: { state: true },    // onderdelen waarvan de uitleg dicht staat
    _editingName: { state: true },
  };

  constructor() {
    super();
    this.templateId = 'new';
    this.editing = null;
    this.loading = true;
    this.saving = false;
    this.error = '';
    this._active = TEMPLATE_SECTIONS[0].id;
    this._closedDesc = new Set();
    this._editingName = false;
    this._snapshot = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has('templateId') && changed.get('templateId') !== undefined) this._load();
    if (changed.has('loading') && !this.loading) this._observeSections();
    if (changed.has('_editingName') && this._editingName) this.renderRoot.querySelector('.name-input')?.focus();
  }

  get _canManage() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  _emptyDefaults() {
    return { project: {}, boring: {}, layer: {}, monster: {}, peilbuis: {}, watermonster: {}, afwerking: { zonderPeilbuis: [], metPeilbuis: [] } };
  }

  async _load() {
    this.error = '';
    if (!this.templateId || this.templateId === 'new') {
      this.editing = { id: null, name: '', defaults: this._emptyDefaults() };
      this._editingName = true;
      this._afterLoad();
      return;
    }
    this.loading = true;
    try {
      const tpl = await api.getTemplate(this.templateId);
      const d = tpl.defaults || {};
      this.editing = {
        id: tpl.id,
        name: tpl.name || '',
        defaults: {
          ...d,
          project: { ...(d.project || {}) },
          boring: { ...(d.boring || {}) },
          layer: { ...(d.layer || {}) },
          monster: { ...(d.monster || {}) },
          peilbuis: { ...(d.peilbuis || {}) },
          // Werd in de vorige editor niet ingelezen: opslaan gooide de watermonsterwaarden weg.
          watermonster: { ...(d.watermonster || {}) },
          afwerking: {
            zonderPeilbuis: Array.isArray(d.afwerking?.zonderPeilbuis) ? d.afwerking.zonderPeilbuis.map(r => ({ ...r })) : [],
            metPeilbuis: Array.isArray(d.afwerking?.metPeilbuis) ? d.afwerking.metPeilbuis.map(r => ({ ...r })) : [],
          },
        },
      };
      this._editingName = false;
    } catch (e) {
      this.error = `Kon sjabloon niet laden: ${e.message}`;
      this.editing = { id: null, name: '', defaults: this._emptyDefaults() };
    }
    this._afterLoad();
  }

  _afterLoad() {
    this._snapshot = JSON.stringify(this.editing);
    this.loading = false;
    this._announceName();
  }

  get _dirty() {
    return !!this.editing && JSON.stringify(this.editing) !== this._snapshot;
  }

  _announceName() {
    window.dispatchEvent(new CustomEvent(TEMPLATE_NAME_EVENT, { detail: { name: this.editing?.name || '' } }));
  }

  /** Houd bij welk onderdeel in beeld is, voor de inhoudsopgave. */
  _observeSections() {
    this._observer?.disconnect();
    const sections = [...this.renderRoot.querySelectorAll('section[data-id]')];
    if (!sections.length || typeof IntersectionObserver === 'undefined') return;
    const visible = new Map();
    this._observer = new IntersectionObserver((entries) => {
      for (const e of entries) visible.set(e.target.dataset.id, e.isIntersecting ? e.boundingClientRect.top : null);
      // Het bovenste onderdeel dat (deels) in beeld is.
      const top = TEMPLATE_SECTIONS.map((s) => s.id).find((id) => visible.get(id) != null);
      if (top && !this._scrollingTo) this._active = top;
    }, { rootMargin: '-80px 0px -55% 0px' });
    sections.forEach((s) => this._observer.observe(s));
  }

  _goSection(id) {
    this._active = id;
    // Tijdens het scrollen niet laten terugspringen door de observer.
    this._scrollingTo = id;
    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => { this._scrollingTo = null; }, 700);
    this.renderRoot.querySelector(`section[data-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _navigate(path, state = {}) {
    window.history.pushState(state, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async _cancel() {
    if (this._dirty) {
      const ok = await confirmDialog({
        title: 'Wijzigingen weggooien?',
        message: 'Je hebt dit sjabloon aangepast maar nog niet opgeslagen.',
        confirmLabel: 'Weggooien',
        danger: true,
      });
      if (!ok) return;
    }
    this._navigate('/templates');
  }

  _setD(chapter, field, value) {
    const defaults = { ...this.editing.defaults };
    const ch = { ...(defaults[chapter] || {}) };
    if (value === '' || value === null || value === undefined || value === false) delete ch[field];
    else ch[field] = value;
    defaults[chapter] = ch;
    this.editing = { ...this.editing, defaults };
  }

  _setName(name) {
    this.editing = { ...this.editing, name };
    this._announceName();
  }

  // --- Afwerking-profielen (percentage + materiaal, van boven naar beneden) ----
  _afwProfile(which) {
    return this.editing?.defaults?.afwerking?.[which] || [];
  }
  _afwMut(which, fn) {
    const defaults = { ...this.editing.defaults };
    const afw = { zonderPeilbuis: [...(defaults.afwerking?.zonderPeilbuis || [])], metPeilbuis: [...(defaults.afwerking?.metPeilbuis || [])] };
    afw[which] = afw[which].map(r => ({ ...r }));
    fn(afw[which]);
    defaults.afwerking = afw;
    this.editing = { ...this.editing, defaults };
  }
  _afwAddRow(which) {
    this._afwMut(which, (a) => {
      // Een nieuwe rij sluit aan op de vorige en loopt door tot 100%; de eerste begint op 0%.
      const prev = a[a.length - 1];
      const van = a.length === 0 ? 0 : (prev && prev.tot !== '' && prev.tot != null ? prev.tot : '');
      const tot = a.length === 0 || (prev && prev.tot !== '' && Number(prev.tot) < 100) ? 100 : '';
      a.push({ van, tot, materiaal: '' });
    });
  }
  _afwUpdRow(which, i, field, value) {
    this._afwMut(which, (a) => {
      if (!a[i]) return;
      a[i][field] = value;
      // 'Tot' van een rij is 'van' van de volgende: zo blijven ze aansluiten.
      if (field === 'tot' && a[i + 1]) a[i + 1].van = value;
    });
  }
  _afwDelRow(which, i) { this._afwMut(which, (a) => a.splice(i, 1)); }

  async _save() {
    const name = (this.editing.name || '').trim();
    if (!name) {
      this.error = 'Geef het sjabloon een naam.';
      this._editingName = true;
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      // Normaliseer de afwerking-profielen: van%/tot% → getal, onvolledige rijen weg.
      const cleanProfile = (rows) => (rows || [])
        .map(r => ({
          van: (r.van === '' || r.van == null) ? null : Number(r.van),
          tot: (r.tot === '' || r.tot == null) ? null : Number(r.tot),
          materiaal: r.materiaal || null,
        }))
        .filter(r => r.van != null && r.tot != null && r.materiaal);
      const defaults = {
        ...this.editing.defaults,
        afwerking: {
          zonderPeilbuis: cleanProfile(this.editing.defaults.afwerking?.zonderPeilbuis),
          metPeilbuis: cleanProfile(this.editing.defaults.afwerking?.metPeilbuis),
        },
      };
      const payload = { name, defaults };
      if (this.editing.id) await api.updateTemplate(this.editing.id, payload);
      else await api.createTemplate(payload);
      this._snapshot = JSON.stringify(this.editing);
      // Het overzicht meldt dat het gelukt is (zie templates-page).
      this._navigate('/templates', { savedTemplate: name });
    } catch (e) {
      this.error = `Fout bij opslaan: ${e.message}`;
    } finally {
      this.saving = false;
    }
  }

  // ── Velden ─────────────────────────────────────────────────────────────────
  _renderField(sec, f) {
    const d = this.editing.defaults;
    const val = fieldValue(d, sec.id, f);
    const set = isFieldSet(d, sec.id, f);
    const change = (v) => this._setD(sec.id, f.k, v);
    let control;
    if (f.kind === 'toggle') {
      control = html`
        <button type="button" class="toggle ${val ? 'on' : ''}" role="switch" aria-checked=${val ? 'true' : 'false'}
          @click=${() => change(val ? '' : true)}>
          <span class="track"><span class="knob"></span></span>
          <span class="t-label">${f.label}</span>
        </button>`;
    } else if (f.kind === 'select') {
      const groups = [...new Set(f.options.map((o) => o.group).filter(Boolean))];
      const opt = (o) => html`<option value=${o.value} ?selected=${o.value === val}>${o.label}</option>`;
      control = html`
        <div class="sel">
          <select class="ctl ${set ? '' : 'unset'}" @change=${(e) => change(e.target.value)}>
            <option value="" ?selected=${!set}>— geen default —</option>
            ${groups.length
              ? groups.map((g) => html`<optgroup label=${g}>${f.options.filter((o) => o.group === g).map(opt)}</optgroup>`)
              : f.options.map(opt)}
            ${set && !f.options.some((o) => o.value === val) ? html`<option value=${val} selected>${val}</option>` : ''}
          </select>
          <span class="chev">${unsafeSVG(lucideIcon(ChevronDown, 16))}</span>
        </div>`;
    } else if (f.kind === 'search') {
      control = html`
        <searchable-select placeholder="— geen default —" .options=${f.options} .value=${val}
          @change=${(e) => change(e.detail.value)}></searchable-select>`;
    } else if (f.kind === 'seg') {
      const opts = [{ value: '', label: 'Geen default' }, ...f.options];
      control = html`
        <div class="seg" role="radiogroup" aria-label=${f.label}>
          ${opts.map((o) => html`
            <button type="button" role="radio" aria-checked=${o.value === val ? 'true' : 'false'}
              class="${o.value === val ? 'on' : ''} ${o.value === '' ? 'none' : ''}"
              @click=${() => change(o.value)}>${o.label}</button>`)}
        </div>`;
    } else {
      const input = html`
        <input class="ctl ${set ? '' : 'unset'}" type=${f.kind === 'number' ? 'number' : 'text'} .value=${val}
          placeholder=${f.placeholder || ''} @input=${(e) => change(e.target.value)} />`;
      control = f.unit ? html`<div class="unit-wrap">${input}<span class="unit">${f.unit}</span></div>` : input;
    }
    return html`
      <div class="field ${f.wide ? 'wide' : ''}">
        ${f.kind !== 'toggle' ? html`
          <div class="f-head">
            <span class="dot ${set ? 'on' : ''}"></span>
            <label>${f.label}</label>
            <span class="grow"></span>
            ${set ? html`<button type="button" class="clear" @click=${() => change('')}>Wissen</button>` : ''}
          </div>` : ''}
        ${control}
        ${f.hint ? html`<span class="hint">${f.hint}</span>` : ''}
      </div>`;
  }

  _renderProfile(which, label) {
    const rows = this._afwProfile(which);
    const val = validateProfile(rows);
    const segs = profileSegments(rows);
    return html`
      <div class="profile">
        <div class="bar-col">
          <span class="pct">0%</span>
          <div class="bar ${rows.length ? '' : 'empty'}">
            ${segs.map((g) => html`<div class="seg-fill" style="top:${g.start}%; height:${g.size}%; background:${g.color};"></div>`)}
          </div>
          <span class="pct">100%</span>
        </div>
        <div class="p-body">
          <div class="p-title">${label}</div>
          ${rows.length ? html`
            <div class="rows">
              <span class="th">VAN %</span><span class="th">TOT %</span><span class="th">MATERIAAL</span><span></span>
              ${rows.map((r, i) => html`
                <input type="number" min="0" max="100" .value=${r.van ?? ''} placeholder="0"
                  @input=${(e) => this._afwUpdRow(which, i, 'van', e.target.value)} />
                <input type="number" min="0" max="100" .value=${r.tot ?? ''} placeholder="100"
                  @input=${(e) => this._afwUpdRow(which, i, 'tot', e.target.value)} />
                <div class="mat">
                  <span class="swatch" style="background:${fillColor(r.materiaal) || 'var(--mv-surface-alt)'}"></span>
                  <select @change=${(e) => this._afwUpdRow(which, i, 'materiaal', e.target.value)}>
                    <option value="">— kies —</option>
                    ${FILL_MATERIALS.map((v) => html`<option value=${v.code} ?selected=${v.code === r.materiaal}>${v.label}</option>`)}
                  </select>
                </div>
                <button type="button" class="del" title="Verwijderen" @click=${() => this._afwDelRow(which, i)}>${unsafeSVG(lucideIcon(X, 15))}</button>
              `)}
            </div>
            <div class="msg ${val.ok ? 'ok' : 'bad'}">${val.msg}</div>
          ` : html`<div class="none">Nog geen trajecten — voeg er een toe.</div>`}
          <button type="button" class="add" @click=${() => this._afwAddRow(which)}>${unsafeSVG(lucideIcon(Plus, 14))} Traject</button>
        </div>
      </div>`;
  }

  _renderSection(sec, i) {
    const c = sectionCounts(this.editing.defaults, sec);
    const open = !this._closedDesc.has(sec.id);
    const toggleDesc = () => {
      const next = new Set(this._closedDesc);
      open ? next.add(sec.id) : next.delete(sec.id);
      this._closedDesc = next;
    };
    return html`
      <section data-id=${sec.id}>
        <div class="sec-head">
          <span class="num">${i + 1}</span>
          <h2>${sec.title}</h2>
          <span class="count ${c.set ? 'on' : ''}">${c.set ? `${c.set} van ${c.total} ingesteld` : 'Geen defaults'}</span>
          <button type="button" class="uitleg" aria-expanded=${open ? 'true' : 'false'} @click=${toggleDesc}>
            Uitleg ${unsafeSVG(lucideIcon(open ? ChevronUp : ChevronDown, 14))}
          </button>
        </div>
        ${open ? html`<p class="desc">${sec.desc ?? html`${sec.descPre}<b>${sec.descBold}</b>${sec.descPost}`}</p>` : ''}
        ${sec.id === 'afwerking'
          ? html`<div class="profiles">${AFWERKING_PROFILES.map(([w, l]) => this._renderProfile(w, l))}</div>`
          : sec.groups.map((g) => html`
              <div class="group">
                ${g.title ? html`<div class="group-title">${g.title}</div>` : ''}
                <div class="grid">${g.fields.map((f) => this._renderField(sec, f))}</div>
              </div>`)}
      </section>`;
  }

  _renderIndex() {
    let set = 0, total = 0;
    const items = TEMPLATE_SECTIONS.map((sec, i) => {
      const c = sectionCounts(this.editing.defaults, sec);
      set += c.set; total += c.total;
      return html`
        <button type="button" class="idx ${sec.id === this._active ? 'on' : ''} ${c.set ? 'set' : ''}" @click=${() => this._goSection(sec.id)}>
          <span class="n">${i + 1}</span>
          <span class="t">${sec.title}</span>
          <span class="c">${c.set ? `${c.set}/${c.total}` : '—'}</span>
        </button>`;
    });
    return html`
      <div class="index">
        <div class="index-head"><span class="eyebrow">Onderdelen</span><span class="total">${set} van ${total} ingesteld</span></div>
        ${items}
      </div>`;
  }

  render() {
    if (this.loading) return html`<div class="loading">Laden…</div>`;
    const e = this.editing;
    const dirty = this._dirty;
    const endName = () => { this._editingName = false; };
    return html`
      <div class="layout">
        <div class="main">
          <button type="button" class="back" @click=${() => this._cancel()}>
            ${unsafeSVG(lucideIcon(ArrowLeft, 16))} Boringsjablonen
          </button>
          <div class="head">
            <div class="eyebrow">${e.id ? 'Sjabloon bewerken' : 'Nieuw sjabloon'}</div>
            ${this._editingName ? html`
              <input class="name-input" .value=${e.name} placeholder="Bijv. Verkennend onderzoek RD/NAP"
                @input=${(ev) => this._setName(ev.target.value)}
                @blur=${() => { if ((this.editing.name || '').trim()) endName(); }}
                @keydown=${(ev) => { if (ev.key === 'Enter' || ev.key === 'Escape') ev.target.blur(); }} />
            ` : html`
              <button type="button" class="name-btn" title="Naam wijzigen" @click=${() => { this._editingName = true; }}>
                <span class="name-text ${e.name ? '' : 'empty'}">${e.name || 'Naamloos sjabloon'}</span>
                ${unsafeSVG(lucideIcon(Pencil, 18))}
              </button>`}
            <p class="lead">Een sjabloon vult standaardwaarden vooraf in bij het aanmaken van een project en bij het digitaliseren. Laat een veld leeg voor géén default; ingevulde waarden blijven altijd aanpasbaar.</p>
          </div>
          ${TEMPLATE_SECTIONS.map((sec, i) => this._renderSection(sec, i))}
        </div>

        <aside>
          ${this._renderIndex()}
          <div class="buttons">
            ${this._canManage ? html`
              <button type="button" class="save" ?disabled=${!dirty || this.saving} @click=${() => this._save()}>
                ${this.saving ? 'Opslaan…' : 'Opslaan'}
              </button>` : ''}
            <button type="button" class="cancel" @click=${() => this._cancel()}>Annuleren</button>
            ${this.error ? html`<div class="error">${this.error}</div>` : ''}
            ${dirty ? html`<div class="unsaved">Niet-opgeslagen wijzigingen</div>` : ''}
          </div>
        </aside>
      </div>
    `;
  }
}

customElements.define('template-editor', TemplateEditor);
